import React from "react";
import ReactDOM from "react-dom";
import { ICellRendererComp, ICellRendererParams } from "ag-grid-community";
import ErrorIcon from "@material-ui/icons/Error";
import Tooltip from "@material-ui/core/Tooltip";
import theme from "styles/theme";
import { extractHashtagsWithIndices } from "components/draft-js-mention/utils/hashtag";
import { SeverityMessages, View, Kinds } from "constants/product-collection";
import { AG_GRID_AUTOCOLUM, getServerSideAggregateValueFormatted, renderGroupCount } from "./render-utils";
import { UNSET_GROUP_KEY } from "helpers/server-rollup";
import { createMarkup } from "lib/helpers";

const alertColorBySeverity = {
    [SeverityMessages.Error]: theme.palette.error,
    [SeverityMessages.Warning]: theme.palette.warningAlertIconColor,
};

const styles = {
    tooltip: {
        fontSize: "11px",
    },
    tooltipWithErrorStatus: {
        fontSize: "11px",
        cursor: "pointer",
    },
    wrapperWithError: {
        display: "flex",
        justifyContent: "space-between",
    },
};

interface IProps extends ICellRendererParams {
    onSearchChange: (e) => void;
}

class DefaultRendererVanilla implements ICellRendererComp {
    declare eGui: HTMLElement;
    declare eValue: HTMLElement;
    declare eErrorTooltip: HTMLElement;

    private getErrorTooltipHtml(alertColor: string, message): HTMLSpanElement | undefined {
        const tooltipContainer = document.createElement("span");
        const TitleComponent = React.createElement("div", { style: styles.tooltip }, message);
        const ErrorIconComponent = React.createElement(ErrorIcon, { htmlColor: alertColor, fontSize: "large" });
        const TooltipComponent = React.createElement(
            Tooltip,
            // @ts-ignore material-ui bug. Have to explicitly pass children property instead of assigning children via React.createElement's 3rd param.
            {
                title: TitleComponent,
                placement: "top-start",
            },
            ErrorIconComponent
        );

        try {
            ReactDOM.render(TooltipComponent, tooltipContainer);
        } catch {
            // no-op
        }

        return tooltipContainer;
    }

    private getErrorStatusTooltipHtml(alertColor: string, message, rowId, kind, context): HTMLSpanElement | undefined {
        const tooltipContainer = document.createElement("span");
        const errorStatus = kind === Kinds.EXPORT_ITEM_VALIDATION ? 0 : 1;
        const query = {
            validationRowId: rowId,
            errorStatus,
            validationLogView: View.ValidationErrorLog,
        };
        const linkElement = React.createElement(
            "a",
            {
                style: styles.tooltipWithErrorStatus,
                onClick: () => context?.onChangeErrorLogLocation(query),
            },
            message
        );
        const TitleComponent = React.createElement("div", { style: styles.tooltip }, linkElement);
        const ErrorIconComponent = React.createElement(ErrorIcon, { htmlColor: alertColor, fontSize: "large" });
        const TooltipComponent = React.createElement(
            Tooltip,
            // @ts-ignore material-ui bug. Have to explicitly pass children property instead of assigning children via React.createElement's 3rd param.
            {
                title: TitleComponent,
                placement: "top-start",
                interactive: true,
            },
            ErrorIconComponent
        );

        try {
            ReactDOM.render(TooltipComponent, tooltipContainer);
        } catch {
            // no-op
        }

        return tooltipContainer;
    }

    private getAlertInfoComponents(value: string, alertInfo: any = {}, context?: any) {
        const { hasAlertInfo, message, severity, rowId, kind } = alertInfo;
        const alertColor = hasAlertInfo
            ? alertColorBySeverity[severity] || alertColorBySeverity[SeverityMessages.Warning]
            : null;
        const AlertInfoWrapper = document.createElement("span");
        Object.keys(styles.wrapperWithError).forEach((styleKey) => {
            AlertInfoWrapper.style[styleKey] = styles.wrapperWithError[styleKey];
        });

        const valueElement = document.createElement("span");
        valueElement.textContent = value;
        AlertInfoWrapper.appendChild(valueElement);
        if (rowId) {
            AlertInfoWrapper.appendChild(this.getErrorStatusTooltipHtml(alertColor, message, rowId, kind, context));
        } else {
            AlertInfoWrapper.appendChild(this.getErrorTooltipHtml(alertColor, message));
        }

        return [AlertInfoWrapper];
    }

    private renderValue(params: IProps) {
        const { node, colDef, formatValue, value, context, column, api } = params;
        const { getAlertInfo } =
            (typeof colDef.cellRendererParams === "function"
                ? colDef.cellRendererParams(params)
                : colDef.cellRendererParams) || {};

        // @ts-ignore
        const { onSearchChange, isServerSide } = api.gridOptionsWrapper.getDefaultColDef()?.cellRendererParams ?? {};
        const alertInfo = (getAlertInfo && getAlertInfo(value, node)) || {};
        const { hasAlertInfo } = alertInfo;

        let formattedValue = "";
        const renderer = colDef.cellRenderer ?? colDef.cellRendererSelector?.(params).component;
        if (node.group && renderer !== "agGroupCellRenderer") {
            if (node.aggData?.[colDef.colId]) {
                formattedValue = formatValue(value);
            }
        } else {
            formattedValue = formatValue(value);
        }

        if (isServerSide) {
            if (colDef.aggFunc || column?.isAnyFunctionActive()) {
                formattedValue = getServerSideAggregateValueFormatted(params, formattedValue);
            } else if (formattedValue === UNSET_GROUP_KEY) {
                formattedValue = node.group && colDef.colId === AG_GRID_AUTOCOLUM ? "" : null;
            }
        }

        const tags = extractHashtagsWithIndices(formattedValue);
        const parts = [];
        let cursor = 0;

        if (tags.length) {
            tags.forEach(({ indices: [start, end] }, i) => {
                const tagValue = formattedValue.slice(start, end);
                if (start !== 0) {
                    parts.push(document.createElement("span"));
                    parts[parts.length - 1].innerHTML = createMarkup(formattedValue.slice(cursor, start));
                }

                const anchorElement = document.createElement("a");
                anchorElement.className = "hashTag";
                anchorElement.setAttribute("data-search-text", tagValue);
                anchorElement.textContent = tagValue;
                if (onSearchChange) {
                    anchorElement.onclick = onSearchChange;
                }

                parts.push(anchorElement);
                cursor = end;
            });

            if (cursor <= formattedValue.length) {
                parts.push(document.createElement("span"));
                parts[parts.length - 1].innerHTML = createMarkup(formattedValue.slice(cursor));
            }
        } else if (hasAlertInfo) {
            // append alert icon
            this.getAlertInfoComponents(formattedValue, alertInfo, context).forEach((alertInfoComponent) => {
                parts.push(alertInfoComponent);
            });
        } else {
            parts.push(document.createElement("span"));
            parts[parts.length - 1].textContent = Array.isArray(formattedValue)
                ? formattedValue.join(", ")
                : formattedValue;
        }

        const itemCount = renderGroupCount(params, true);
        itemCount && parts.push(itemCount);

        // Clear children elements
        while (this.eValue.lastElementChild) {
            this.eValue.removeChild(this.eValue.lastElementChild);
        }
        while (parts.length > 0) {
            this.eValue.appendChild(parts.shift());
        }
    }

    private buildBaseElement() {
        // create the cell
        this.eGui = document.createElement("div");
        this.eGui.style.width = "100%";
        this.eGui.innerHTML = '<span class="value"></span>';

        // get references to the elements we want
        this.eValue = this.eGui.querySelector(".value");
    }

    refresh(params: IProps): boolean {
        // set value into cell again
        this.renderValue(params);

        // return true to tell the grid we refreshed successfully
        return true;
    }
    getGui(): HTMLElement {
        return this.eGui;
    }
    destroy(): void {
        // No-op
    }
    init(params: IProps) {
        this.buildBaseElement();

        // set value into cell
        this.renderValue(params);
    }
}

export { DefaultRendererVanilla };
